import { Link } from "react-router-dom";
import {
  ExternalLink, Globe, LayoutTemplate, MapPin, PenLine, Sparkles, TrendingUp, Wand2,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useSelfServiceCreation } from "../features/capabilities/SelfServiceCreation";
import CancelBuildButton from "../components/CancelBuildButton";
import { canonicalSites } from "../components/SiteGallery";
import SessionUnavailable from "../components/SessionUnavailable";
import { forgePreviewUrl, siteHost, siteUrl } from "../lib/domains";
import { SavedPreviewImage } from "../components/SavedPreviewImage";
import { siteDisplayState } from "../lib/siteLifecycle";
import { useLiveSites } from "../lib/useLiveSites";

// Ported from the Dashboard.dc.html home view (see styles/dashboard-design.css)
// with owner-directed layout revisions on top (2026-08-03):
// - The two creation entries — "Find my business" and "Start from scratch" —
//   are ALWAYS visible in the page header. The design file only showed them in
//   the empty state, which meant an account with sites had no way to start
//   another one from the dashboard at all.
// - Visitors and SEO performance live in a RIGHT sidebar; the managing card,
//   site chips, and site list are the main column (owner: left rail read as
//   too busy).
// Standing rules unchanged: "ProtoPal Studio" lives in the topbar brand, and
// panels the prototype faked (visitors chart, SEO keywords, +N deltas) render
// honest states inside the same design frame.
export default function Dashboard() {
  const auth = useAuth();
  const showCreation = useSelfServiceCreation();
  const { sites: allSites, error, refresh } = useLiveSites();
  if (auth.status === "error") return <SessionUnavailable retry={auth.retry} />;
  if (auth.status !== "authed") return null;
  const sites = allSites === null ? null : canonicalSites(allSites.filter(site => site.ownerUserId === auth.me.userId && !site.archivedAt));
  const displayName = auth.me.displayName?.trim() || auth.me.email || "there";
  const firstName = displayName.split(/\s+/)[0] || displayName;
  const hour = new Date().getHours();
  const daypart = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";

  const current = sites?.[0] ?? null;
  const currentBuild = current ? current.previewBuildId ?? current.liveBuildId : null;
  const currentState = current ? siteDisplayState(current) : null;
  const live = sites?.filter((site) => siteDisplayState(site) === "active").length ?? 0;
  const isEmpty = sites !== null && (sites?.length ?? 0) === 0;

  return (
    <div className="studio-page dashboard-page">
      <div className="dd-head">
        <div>
          <div className="dd-eyebrow">{isEmpty ? `Welcome, ${firstName}` : `Good ${daypart}, ${firstName}`}</div>
          <h1 className="dd-display">{isEmpty ? <>Welcome to <b>ProtoPal</b></> : <>Welcome back to <b>ProtoPal</b></>}</h1>
        </div>
        {!isEmpty && showCreation && (
          <div className="dd-head-ctas">
            <Link to="/start/business" className="dd-cta-primary"><MapPin size={16} /> Find my business</Link>
            <Link to="/start/scratch" className="dd-cta-ghost"><PenLine size={16} /> New site from scratch</Link>
          </div>
        )}
      </div>

      <section className="dd-card">
        {error && <div className="notice error" role="alert">Your websites could not load. <button className="btn" onClick={() => void refresh()}>Try again</button></div>}
        {sites === null && !error && <div className="dd-seo-empty">Loading your websites…</div>}

        {isEmpty && (
          <>
            <div className="dd-card-head">
              <div>
                <div className="dd-card-title">Dashboard</div>
                <div className="dd-card-sub">{showCreation ? "Your websites will show up here once you build your first one." : "Your websites will show up here once our team sets them up."}</div>
              </div>
            </div>
            <div className="dd-empty">
              <div className="dd-empty-art">
                <div className="dd-empty-art-main"><LayoutTemplate size={40} /></div>
                <div className="dd-empty-art-badge"><Sparkles size={17} /></div>
              </div>
              <h2>{showCreation ? "Let’s build your first website" : "Your first website is in good hands"}</h2>
              <p>{showCreation ? "Tell us about your business and ProtoPal builds a complete, published site in minutes, copy, pages, and photos included." : "The ProtoPal team prepares your website. Once it is ready, you can edit and publish it here."}</p>
              {showCreation && <div className="dd-empty-actions">
                <Link to="/start/business" className="dd-cta-primary"><MapPin size={16} /> Find my business</Link>
                <Link to="/start/scratch" className="dd-cta-ghost"><PenLine size={16} /> Start from scratch</Link>
              </div>}
            </div>
          </>
        )}

        {current && (
          <div className="dd-columns">

            <div className="dd-main">
            <div className="dd-sites-label">
              <span className="dd-sites-caps">My website</span>
              <span className="dd-counts">
                {sites?.length ?? 0} generated · {live} live
                <Link to="/sites" className="dd-link">All websites</Link>
              </span>
            </div>
            <article className="dd-site">
              <div className="dd-site-media">
                <SavedPreviewImage key={current.id} buildId={currentBuild} fallbackBuildId={current.liveBuildId} label={siteHost(current.subdomain)} />
                <span className={`dd-site-status ${currentState === "active" ? "" : currentState === "building" ? "building" : "draft"}`}>
                  {currentState === "active" ? "Live" : currentState === "building" ? "Building" : currentState === "draft" ? "Draft" : currentState === "needs-attention" ? "Needs attention" : "Setup not finished"}
                </span>
              </div>
              <div className="dd-site-body">
                <div>
                  <div className="dd-site-name">{siteHost(current.subdomain)}</div>
                  {current.status === "active" ? (
                    <a className="dd-site-domain" href={siteUrl(current.subdomain)} target="_blank" rel="noreferrer"><Globe size={13} />{siteHost(current.subdomain)}</a>
                  ) : currentBuild ? (
                    <a className="dd-site-domain" href={forgePreviewUrl(currentBuild)} target="_blank" rel="noreferrer"><Globe size={13} />Private preview</a>
                  ) : null}
                </div>
                <div className="dd-site-stats">
                  <div>
                    <div className="dd-site-stat-caps">Visitors · 30d</div>
                    <div className="dd-site-stat-value">Arrives with production</div>
                  </div>
                  <div className="dd-site-divider" />
                  <div>
                    <div className="dd-site-stat-caps">Status</div>
                    <div className="dd-site-stat-value">
                      {currentState === "active"
                        ? `Published${current.publishedAt ? ` ${new Date(current.publishedAt).toLocaleDateString()}` : ""}`
                        : currentState === "building" ? "Build in progress"
                        : currentState === "draft" ? "Draft workspace"
                        : currentState === "needs-attention" ? "Build didn't finish"
                        : "Setup not finished"}
                    </div>
                  </div>
                </div>
                <div className="dd-site-actions">
                  {currentState === "building" ? (
                    <>
                      {current.pendingBuildId && <Link className="dd-cta-primary" to={`/sites/${current.id}/builds/${current.pendingBuildId}`}>Watch progress</Link>}
                      <CancelBuildButton site={current} onCancelled={() => void refresh()} />
                    </>
                  ) : currentState === "active" ? (
                    <>
                      <Link className="dd-cta-primary" to={`/sites/${current.id}/editor`}><PenLine size={16} />Edit website</Link>
                      <a className="dd-cta-ghost" href={siteUrl(current.subdomain)} target="_blank" rel="noreferrer"><ExternalLink size={16} />View live</a>
                    </>
                  ) : currentState === "draft" ? (
                    <>
                      <Link className="dd-cta-primary" to={`/sites/${current.id}/editor`}><PenLine size={16} />Edit website</Link>
                      <Link className="dd-cta-ghost" to={`/sites/${current.id}`}><ExternalLink size={16} />Review &amp; publish</Link>
                    </>
                  ) : (
                    // no-build / needs-attention: the editor has nothing to open,
                    // so route to the workspace whose hero IS the next step
                    // ("Build my site" / "Try build again"). Without this a
                    // stuck draft dead-ends on a broken "Edit website".
                    <Link className="dd-cta-primary" to={`/sites/${current.id}`}>
                      <Wand2 size={16} />{currentState === "needs-attention" ? "Finish your site" : "Continue setup"}
                    </Link>
                  )}
                </div>
              </div>
            </article>

            <div className="dd-callout">
              <div className="dd-callout-icon"><Wand2 size={19} /></div>
              <div style={{ flex: 1, lineHeight: 1.4 }}>
                <div className="dd-callout-title">Want your site polished to perfection?</div>
                <div className="dd-callout-sub">Our team can fine-tune any page — tell us what you have in mind.</div>
              </div>
              <a className="dd-pill-ghost" href="mailto:dev@prototypical.ai">Talk to us</a>
            </div>
            </div>

            <aside className="dd-rail">
              <div className="dd-stat">
                <div className="dd-stat-row">
                  <div>
                    <span className="dd-stat-caps">Visitors</span>
                    <div className="dd-stat-value"><span className="dd-stat-big">—</span></div>
                  </div>
                  <span className="dd-legend"><i />Last 30 days</span>
                </div>
                <div className="dd-chart-empty">Traffic analytics arrive with production. Your site is already being served — the counting starts then.</div>
                <div className="dd-chart-weeks"><span>Wk 1</span><span>Wk 2</span><span>Wk 3</span><span>Wk 4</span></div>
              </div>

              <div className="dd-seo">
                <div className="dd-seo-head">
                  <div className="dd-seo-title-wrap">
                    <span className="dd-seo-icon"><TrendingUp size={17} /></span>
                    <div>
                      <div className="dd-seo-title">SEO performance</div>
                      <div className="dd-seo-sub">Keyword rankings for {siteHost(current.subdomain)} on Google</div>
                    </div>
                  </div>
                </div>
                <div className="dd-seo-empty">
                  Keyword tracking arrives with production. Every ProtoPal site already ships SEO-ready — titles, descriptions, and structure included.
                </div>
              </div>
            </aside>
          </div>
        )}
      </section>
    </div>
  );
}
